import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack
} from '@mui/material';
import {
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Unsubscribe as UnsubscribeIcon,
  MarkEmailRead as ResubscribeIcon
} from '@mui/icons-material';
import AdminPageLayout from '../components/admin/AdminPageLayout';
import { leadService, LEAD_STATUSES, LEAD_STATUS_COLORS } from '../services/leadService';
import logger from '../utils/logger';

const PAGE_SIZE = 50;

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/** Full office address on one line, or an em dash when we never sourced one. */
const formatAddress = (lead) => {
  if (!lead.street) return '—';
  return [lead.street, lead.city, [lead.state, lead.zip].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
};

const PropertyManagerLeads = () => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });

  const [filters, setFilters] = useState({ status: '', hasEmail: '', search: '' });
  // Debounced copy of filters.search — typing shouldn't fire a request per keystroke.
  const [searchTerm, setSearchTerm] = useState('');

  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ status: 'new', notes: '' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchTerm }));
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [listResponse, statsResponse] = await Promise.all([
        leadService.listLeads({ ...filters, page, limit: PAGE_SIZE }),
        leadService.getStats()
      ]);

      setLeads(listResponse.leads);
      setPagination(listResponse.pagination);
      setStats(statsResponse);
    } catch (err) {
      logger.log('[PropertyManagerLeads] Failed to load leads:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const handleFilterChange = (field) => (event) => {
    setFilters((prev) => ({ ...prev, [field]: event.target.value }));
    setPage(1);
  };

  const openEditor = (lead) => {
    setEditing(lead);
    setEditForm({ status: lead.status, notes: lead.notes || '' });
    setSaveError(null);
  };

  const toggleUnsubscribe = async (lead) => {
    const nowUnsubscribed = !lead.unsubscribedAt;
    if (
      nowUnsubscribed &&
      !window.confirm(
        `Unsubscribe ${lead.companyName}? They will be excluded from every future outreach campaign.`
      )
    ) {
      return;
    }

    try {
      const updated = await leadService.setUnsubscribed(lead.id, nowUnsubscribed);
      setLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to update subscription');
    }
  };

  const saveEdit = async () => {
    setSaving(true);
    setSaveError(null);

    try {
      const updated = await leadService.updateLead(editing.id, {
        status: editForm.status,
        notes: editForm.notes
      });

      // Patch in place so the row updates without refetching the whole page.
      setLeads((prev) => prev.map((lead) => (lead.id === updated.id ? updated : lead)));
      setEditing(null);

      // Status counts changed, so refresh the summary cards.
      leadService.getStats().then(setStats).catch(() => {});
    } catch (err) {
      setSaveError(err.response?.data?.error || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const summaryCards = stats
    ? [
        { label: 'Total leads', value: stats.total },
        { label: 'With an email', value: stats.withEmail },
        { label: 'Phone only', value: stats.withoutEmail },
        { label: 'Contacted', value: stats.byStatus?.contacted || 0 },
        { label: 'Qualified', value: stats.byStatus?.qualified || 0 }
      ]
    : [];

  return (
    <AdminPageLayout>
      <Typography variant="h4" component="h1" gutterBottom>
        Property Manager Leads
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Outbound B2B prospects imported from CSV. Separate from Questionnaire Leads, which
        are inbound submissions from the website.
      </Typography>

      {stats && (
        <Box display="flex" gap={2} mb={3} flexWrap="wrap">
          {summaryCards.map(({ label, value }) => (
            <Card key={label} sx={{ minWidth: 160, flex: '1 1 160px' }}>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  {label}
                </Typography>
                <Typography variant="h4">{value}</Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <TextField
            label="Search company"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            size="small"
            fullWidth
            sx={{ maxWidth: { md: 320 } }}
          />

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Status</InputLabel>
            <Select value={filters.status} label="Status" onChange={handleFilterChange('status')}>
              <MenuItem value="">All statuses</MenuItem>
              {LEAD_STATUSES.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Email</InputLabel>
            <Select value={filters.hasEmail} label="Email" onChange={handleFilterChange('hasEmail')}>
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="true">Has an email</MenuItem>
              <MenuItem value="false">No email</MenuItem>
            </Select>
          </FormControl>

          <Box flexGrow={1} />

          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={fetchLeads} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel active direction="asc" hideSortIcon>
                      Company
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Website</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last contacted</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell align="right">Edit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leads.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                        No leads match these filters. Import some via Dashboard → Lead Import.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {leads.map((lead) => (
                  <TableRow key={lead.id} hover>
                    <TableCell>{lead.companyName}</TableCell>
                    <TableCell>
                      {lead.email ? (
                        <Link href={`mailto:${lead.email}`}>{lead.email}</Link>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      {lead.phone ? <Link href={`tel:${lead.phone}`}>{lead.phone}</Link> : '—'}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>{formatAddress(lead)}</TableCell>
                    <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {lead.website ? (
                        <Link href={lead.website} target="_blank" rel="noopener noreferrer">
                          {lead.website.replace(/^https?:\/\/(www\.)?/, '')}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Chip
                          label={lead.status}
                          size="small"
                          color={LEAD_STATUS_COLORS[lead.status] || 'default'}
                        />
                        {lead.unsubscribedAt && (
                          <Tooltip title="Excluded from all outreach campaigns">
                            <Chip label="unsubscribed" size="small" color="error" variant="outlined" />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{formatDate(lead.lastContactedAt)}</TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>
                      <Typography variant="body2" noWrap title={lead.notes || ''}>
                        {lead.notes || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Update outreach">
                        <IconButton size="small" onClick={() => openEditor(lead)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={
                          lead.unsubscribedAt
                            ? 'Re-subscribe to campaigns'
                            : 'Unsubscribe from all campaigns'
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={!lead.email}
                            onClick={() => toggleUnsubscribe(lead)}
                          >
                            {lead.unsubscribedAt ? (
                              <ResubscribeIcon fontSize="small" />
                            ) : (
                              <UnsubscribeIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {pagination.totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={pagination.totalPages}
                page={page}
                onChange={(event, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}

          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
            {pagination.total} lead{pagination.total === 1 ? '' : 's'} matching
          </Typography>
        </>
      )}

      <Dialog open={Boolean(editing)} onClose={() => !saving && setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing?.companyName}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Company details come from the import and can't be edited here, so a re-import
            never conflicts with your changes.
          </Typography>

          {saveError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {saveError}
            </Alert>
          )}

          <FormControl fullWidth size="small" sx={{ mb: 2, mt: 1 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={editForm.status}
              label="Status"
              onChange={(event) => setEditForm((prev) => ({ ...prev, status: event.target.value }))}
            >
              {LEAD_STATUSES.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Notes"
            value={editForm.notes}
            onChange={(event) => setEditForm((prev) => ({ ...prev, notes: event.target.value }))}
            fullWidth
            multiline
            minRows={3}
          />

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Moving a lead to “contacted” stamps the last-contacted date automatically.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={saveEdit} variant="contained" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminPageLayout>
  );
};

export default PropertyManagerLeads;
